// --------------------------------------------------------
// APPOINTMENT PANEL - AppointmentPanel
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { Calendar, Day } from '@hassanmojab/react-modern-calendar-datepicker';
import React, { useCallback, useEffect, useState } from 'react'
import { utils } from 'react-modern-calendar-datepicker';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../..';
import CareLineDropdown from '../../components/dropdown/CareLineDropdown';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { GeneralContainer, Text } from '../../components/UI/generalLayout/Layout.styled';
import ScreenHeader from '../../components/UI/screenHeader/ScreenHeader';
import Unit from '../../models/core/unit/Unit';
import * as unitActions from '../../store/actions/unit';
import * as appointmentActions from '../../store/actions/appointment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Colors from '../../constants/Colors';
import { FilledButton } from '../../components/UI/buttons/Button';
import { CircularProgress, InputAdornment, OutlinedInput } from '@mui/material';
import Search from "@mui/icons-material/Search";
import { AppointmentStatusContainer, AppointmentStatusFilterContainer, CalendarAnchor, CalendarRowContainer, FilterContainer, FilterOptionContainer, ListHeaderContainer, ListHeaderItemContainer, ListHeaderItemWithFilterContainer } from './AppointmentPanel.styled';
import AppointmentListItem from '../../components/appointmentListItem/AppointmentListItem';
import Popup from 'reactjs-popup';
import { meses, ptBrCustomLocale } from '../../constants/Objects';
import { ReactComponent as Filter } from '../../assets/imgs/filter.svg'
import { StyledCheckbox } from '../../components/criarAgenda/AgendaMesHorarioListItem.styled';
import EncaixeModal from '../../components/appointmentAttendance/EncaixeModal';
import { CategoryService } from '../../core/http/service/CategoryService';
import BaseResponse from '../../models/core/BaseResponse';
import Category from '../../models/core/careLine/Category';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import { GenericSolidButton, OutlinedUpdateButton } from '../../components/UI/buttons/Button.styled';
import { convertDayToMoment, executeWithDelay } from '../../helpers/HelperFunctions';
import { ReactComponent as EmTriagem } from '../../assets/imgs/coloredWaitingTriagem.svg'
import { ReactComponent as WaitingTriagem } from '../../assets/imgs/coloredWaitingTriagem.svg'
import { ReactComponent as Atendimento } from '../../assets/imgs/atendimento.svg'
import { ReactComponent as Check } from '../../assets/imgs/greenCheck.svg'
import { ReactComponent as CalendarCheck } from '../../assets/imgs/calendarCheck.svg'
import { ReactComponent as Away } from '../../assets/imgs/away.svg'
import { AppointmentStatus } from '../../enums/AppointmentStatus';
import { CompanyListContainer } from '../company/CompanyList.styled';
import moment from 'moment';
import { ReactComponent as Update } from "../../assets/imgs/updateIcon.svg";


// --------------------------------------------------------
// APPOINTMENT PANEL INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */

function AppointmentPanel(): JSX.Element {
    // --------------------------------------------------------
    // APPOINTMENT PANEL PRIVATE DECLARATIONS
    // --------------------------------------------------------
    const [selectedUnit, setSelectedUnit] = useState<any>({ id: '', nome: '' })
    const [selectedDay, setSelectedDay] = useState<any>(utils('en').getToday())
    const [nomeOuProfissionalSearchQuery, setNomeOuProfissionalSearchQuery] = useState('')
    const [activeUnits, setActiveUnits] = useState<any[]>([])
    const [showEncaixeModal, setShowEncaixeModal] = useState(false)
    const [disableEncaixe, setDisableEncaixe] = useState(false)
    const [showAppointmentTypeFilterPopup, setShowAppointmentTypeFilterPopup] = useState<boolean>(false)
    const [appointmentTypeFilterList, setAppointmentTypeFilters] = useState<string[]>([])
    const [appointmentStatusFilter, setAppointmentStatusFilter] = useState<string>('')
    const [categorias, setCategorias] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [fireUpdate, setFireUpdate] = useState(false)
    const dispatch = useAppDispatch()
    const units: Unit[] = useSelector((state: any) => state.unit.units)
    const appointments: any[] = useSelector((state: any) => state.appointment.appointments)
    const hasSelectedUnit = selectedUnit.id !== '';
    const categoryService: CategoryService = new CategoryService();
    const { isMobile } = useResponsiveInfo();

    const fetchUnits = useCallback(async () => {
        await dispatch(unitActions.fetchUnitList());
    }, [])

    const fetchCategories = useCallback(async () => {
        const categories: Category[] = [];
        const categoryResponse: any = await categoryService.getAll()
        const basecategoryResponse: BaseResponse<any> = new BaseResponse().fromJSON(categoryResponse.data)
        basecategoryResponse.data.forEach((d: any) => {
            categories.push(new Category().fromJSON(d))
        })
        setCategorias(categories)
    }, [categoryService])

    useEffect(() => {
        fetchUnits();
        fetchCategories();
    }, [])

    useEffect(() => {
        if (!!units) {
            const unidadesAtivas: any[] = []
            units.forEach((unit: Unit) => {
                if (!unit.inativo) {
                    unidadesAtivas.push({ nome: unit.nomeFantasia, id: unit.id })
                }
            })
            setActiveUnits(unidadesAtivas)
        }
    }, [units])

    const formatSelectedDay = () => {
        return `${selectedDay.day} de ${meses[selectedDay.month - 1]} de ${selectedDay.year}`
    }


    const fetchAppointments = useCallback(async (nomeSearch: string, tipoConsultaSearch: string[], statusPresenca: string) => {
        const formattedDate = `${selectedDay.year}-${selectedDay.month}-${selectedDay.day}`
        await dispatch(appointmentActions.fetchAppointments(selectedUnit.id, formattedDate, nomeSearch, tipoConsultaSearch, statusPresenca, false))
    }, [selectedUnit, selectedDay])

    useEffect(() => {
        if (hasSelectedUnit) {
            setIsLoading(true)
            fetchAppointments('', [], '')
            setTimeout(() => {
                setIsLoading(false)
            }, 800)
        }
    }, [selectedUnit, selectedDay])

    useEffect(() => {
        const today = moment()
        const selectedDayMoment = moment(`${selectedDay.year}-${selectedDay.month}-${selectedDay.day}`)
        if (selectedDayMoment.isAfter(today) || selectedDayMoment.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')) {
            setDisableEncaixe(false)
        } else {
            setDisableEncaixe(true)
        }
    }, [selectedDay])

    useEffect(() => {
        if (nomeOuProfissionalSearchQuery.length >= 3 || nomeOuProfissionalSearchQuery === '') {
            if (hasSelectedUnit) {
                executeWithDelay(500, async () => {
                    fetchAppointments(nomeOuProfissionalSearchQuery, [], '')
                })
            }
        }
    }, [nomeOuProfissionalSearchQuery])

    useEffect(() => {
        if (hasSelectedUnit) {
            setIsLoading(true)
            fetchAppointments('', appointmentTypeFilterList, appointmentStatusFilter.toLowerCase())
            setTimeout(() => {
                setIsLoading(false)
            }, 800)
        }
    }, [appointmentStatusFilter, hasSelectedUnit, appointmentTypeFilterList, fireUpdate])

    const getMaximumDate = () => {
        const day = utils('en').getToday();
        const momentDate = moment(`${day.year}-${day.month}-${day.day}`, 'YYYY-MM-DD').add(30, 'days')
        const maxDate: Day = { year: momentDate.year(), month: momentDate.month() + 1, day: Number(momentDate.format('D')) };
        return maxDate;
    }

    const getMinDate = () => {
        const day = utils('en').getToday();
        const momentDate = moment(`${day.year}-${day.month}-${day.day}`, 'YYYY-MM-DD').subtract(30, 'days')
        const minDate: Day = { year: momentDate.year(), month: momentDate.month() + 1, day: Number(momentDate.format('D')) };
        return minDate;
    }

    const PopupCalendar = () => {
        const maxDate = getMaximumDate()
        const minDate = getMinDate()

        return (
            <Popup
                position="bottom center"
                nested
                trigger={
                    <CalendarAnchor isMobile={isMobile}>
                        {formatSelectedDay()}
                        <KeyboardArrowDownIcon />
                    </CalendarAnchor>}
            >
                <Calendar
                    value={selectedDay}
                    onChange={setSelectedDay}
                    locale={ptBrCustomLocale}
                    maximumDate={maxDate}
                    minimumDate={minDate}
                    colorPrimary={Colors.SPM600}
                    colorPrimaryLight={Colors.SPM50}
                    shouldHighlightWeekends
                />
            </Popup>)
    }

    const insertIntoFilterList = (value: string) => {
        setAppointmentTypeFilters((prev: string[]) => {
            const list = [...prev]
            const index = list.indexOf(value)
            if (index !== -1) {
                list.splice(index, 1)
            } else {
                list.push(value)
            }
            return list
        })
    }

    const PopupTipoFilter = () => {
        return (
            <Popup
                position="bottom center"
                nested
                closeOnDocumentClick={false}
                closeOnEscape={false}
                trigger={<Filter onClick={() => { setShowAppointmentTypeFilterPopup(true) }} style={{ marginTop: 0, marginLeft: 10, cursor: 'pointer' }} />}
            >
                <FilterContainer>
                    <FilterOptionContainer>
                        <StyledCheckbox checked={appointmentTypeFilterList.includes('clinica')} onChange={(event: any) => { insertIntoFilterList('clinica') }} />
                        <Text style={{ marginTop: 14 }}>Consulta clínica</Text>
                    </FilterOptionContainer>
                    <FilterOptionContainer>
                        <StyledCheckbox checked={appointmentTypeFilterList.includes('periodica')} onChange={(event: any) => { insertIntoFilterList('periodica') }} />
                        <Text style={{ marginTop: 14 }}>Consulta periódica</Text>
                    </FilterOptionContainer>
                    <FilterOptionContainer>
                        <StyledCheckbox checked={appointmentTypeFilterList.includes('preventiva')} onChange={(event: any) => { insertIntoFilterList('preventiva') }} />
                        <Text style={{ marginTop: 14 }}>Exame preventivo</Text>
                    </FilterOptionContainer>
                </FilterContainer>
            </Popup>)
    }

    const isAppointmentStatusSelected = (status: string) => {
        return status === appointmentStatusFilter;
    }

    const PresencaPopupFilter = () => {
        return (
            <Popup
                position="bottom center"
                nested
                trigger={<Filter style={{ marginTop: 0, marginLeft: 10, cursor: 'pointer' }} />}
            >
                <AppointmentStatusFilterContainer style={{ overflowY: 'scroll' }}>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter(AppointmentStatus.T) }} isSelected={isAppointmentStatusSelected(AppointmentStatus.T)}>
                        <WaitingTriagem style={{ cursor: 'pointer', marginTop: 17, marginRight: 10 }} />
                        <Text>Aguardando triagem</Text>
                    </AppointmentStatusContainer>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter(AppointmentStatus.ET) }} isSelected={isAppointmentStatusSelected(AppointmentStatus.ET)}>
                        <EmTriagem style={{ cursor: 'pointer', marginTop: 17, marginRight: 10 }} />
                        <Text>Em triagem</Text>
                    </AppointmentStatusContainer>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter(AppointmentStatus.A) }} isSelected={isAppointmentStatusSelected(AppointmentStatus.A)}>
                        <Atendimento style={{ marginTop: 12, marginRight: 10, color: 'white', cursor: 'pointer' }} />
                        <Text style={{ fontSize: 14 }}>Aguardando atendimento</Text>
                    </AppointmentStatusContainer>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter(AppointmentStatus.EA) }} isSelected={isAppointmentStatusSelected(AppointmentStatus.EA)}>
                        <Atendimento style={{ cursor: 'pointer', marginTop: 17, marginRight: 10 }} />
                        <Text>Em atendimento</Text>
                    </AppointmentStatusContainer>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter(AppointmentStatus.V) }} isSelected={isAppointmentStatusSelected(AppointmentStatus.V)}>
                        <Check style={{ cursor: 'pointer', marginTop: 15, marginRight: 10 }} />
                        <Text>Realizado</Text>
                    </AppointmentStatusContainer>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter(AppointmentStatus.X) }} isSelected={isAppointmentStatusSelected(AppointmentStatus.X)}>
                        <Away style={{ marginTop: 15, marginRight: 10, color: 'white', cursor: 'pointer' }} />
                        <Text>Ausente</Text>
                    </AppointmentStatusContainer>
                    <AppointmentStatusContainer onClick={() => { setAppointmentStatusFilter('') }} isSelected={isAppointmentStatusSelected('')}>
                        <Text>Todos</Text>
                    </AppointmentStatusContainer>
                </AppointmentStatusFilterContainer>
            </Popup>)
    }

    return (
        <ContainerWithSidebar>
            <GeneralContainer>
                <ScreenHeader
                    screenTitle='Painel de Atendimento'
                    onRightButtonClick={() => { }}
                    hasRightButton={false}
                    rightButtonText={''}
                />
                <div style={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex' }}>
                    <Text style={{ marginRight: 10 }}>Unidade: </Text>
                    <CareLineDropdown
                        placeholder='Selecione a Unidade de Atendimento'
                        style={{ width: isMobile ? '100%' : 310 }}
                        value={selectedUnit}
                        onChange={setSelectedUnit}
                        items={activeUnits}
                        disabled={false}
                    />
                </div>
                <div>
                    <div style={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex', marginTop: 10 }}>
                        <Text>Agendas de </Text>
                        <CalendarRowContainer isMobile={isMobile}>
                            <PopupCalendar />
                            {!isMobile && <FilledButton color={Colors.SPM600} disabled={selectedUnit.id === '' || disableEncaixe} onClick={() => { setShowEncaixeModal(true) }}>Adicionar Encaixe</FilledButton>}
                        </CalendarRowContainer>
                    </div>
                    {isMobile && <GenericSolidButton style={{ marginTop: 10 }} color={Colors.SPM600} disabled={selectedUnit.id === ''} onClick={() => { setShowEncaixeModal(true) }}>Adicionar Encaixe</GenericSolidButton>}
                    <div style={{ flexDirection: isMobile? 'column':'row', display:'flex', justifyContent: 'space-between' }}>
                        <OutlinedInput
                            style={{ borderRadius: "10px", marginTop: 20, width: isMobile ? "100%" : "450px" }}
                            id="outlined-adornment-amount"
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            }
                            label=""
                            placeholder="Buscar por nome de paciente ou profissional"
                            value={nomeOuProfissionalSearchQuery}
                            onChange={(event: any) => {
                                setNomeOuProfissionalSearchQuery(event.target.value)
                            }}
                        />
                        <OutlinedUpdateButton isMobile={isMobile} style={{ height: 20, marginTop: 20 }} onClick={()=>{ setFireUpdate(prev=>!prev) }}>
                            <Update />
                            Atualizar
                        </OutlinedUpdateButton>
                    </div>

                </div>
                {(hasSelectedUnit) && (
                    <div style={{ marginTop: 20 }}>
                        <>
                            {!isMobile && (<ListHeaderContainer>
                                <ListHeaderItemContainer flex={1.5}>
                                    <Text bold style={{ margin: 0 }}>Nome</Text>
                                </ListHeaderItemContainer>
                                <ListHeaderItemContainer width={120} textAlign="center">
                                    <Text bold style={{ margin: 0 }}>Horário</Text>
                                </ListHeaderItemContainer>
                                <ListHeaderItemContainer flex={1} style={{ paddingLeft: 10 }}>
                                    <Text bold style={{ margin: 0 }}>Profissional</Text>
                                </ListHeaderItemContainer>
                                <ListHeaderItemWithFilterContainer flex={1} style={{ paddingLeft: 10 }}>
                                    <Text bold style={{ margin: 0 }}>Tipo</Text>
                                    <PopupTipoFilter />
                                </ListHeaderItemWithFilterContainer>
                                <ListHeaderItemWithFilterContainer width={150} textAlign="right">
                                    <Text bold style={{ margin: 0 }}>Presença</Text>
                                    <PresencaPopupFilter />
                                </ListHeaderItemWithFilterContainer>
                            </ListHeaderContainer>)}
                            {appointments.length === 0 && !isLoading && (<Text>Nenhum resultado encontrado</Text>)}

                            {appointments.length > 0 && !isLoading && (<CompanyListContainer style={{ marginBottom: isMobile ? 30 : 10 }}>
                                <>
                                    {appointments.map(ap => {
                                        return <AppointmentListItem selectedDate={convertDayToMoment(selectedDay)} appointment={ap} updateList={() => { fetchAppointments('', [], '') }} />
                                    })}</>
                            </CompanyListContainer>)}

                            {isLoading && (<div style={{ textAlign: 'center', marginTop: 50, height: 50 }}><CircularProgress /></div>)}
                        </>
                    </div>
                )}
                <EncaixeModal
                    visible={showEncaixeModal}
                    categories={categorias}
                    currentUnitId={selectedUnit.id}
                    currentDate={`${selectedDay.day}/${selectedDay.month}/${selectedDay.year}`}
                    onConfirm={() => { fetchAppointments('', [], ''); setShowEncaixeModal(false) }}
                    onCancel={() => { setShowEncaixeModal(false) }} />
            </GeneralContainer>
        </ContainerWithSidebar>);
}

export default AppointmentPanel;
